type PathArea = {
  id: string;
  name: string;
  path: string;
  parent?: { name: string } | null;
};

type DepthArea = {
  id: string;
  level: { depth: number };
};

export type AreaOption = {
  label: string;
  value: string;
};

const byLabel = (a: AreaOption, b: AreaOption) =>
  a.label.localeCompare(b.label, "es");

/**
 * Options to pick sub-region areas (communes, municipalities...) by id, limited
 * to the selected regions: none when no region is selected. Areas that share a
 * name are told apart by their parent's name.
 *
 * `alreadySelected` are areas a campaign already targets that may not be in
 * `areas` (e.g. a province, while `areas` lists communes): they are offered too
 * while their region is selected, so the picker can show and remove them.
 */
export const buildAreaOptions = (
  areas: ReadonlyArray<PathArea>,
  regions: ReadonlyArray<PathArea>,
  selectedRegionIds: ReadonlyArray<string>,
  alreadySelected: ReadonlyArray<PathArea> = [],
): AreaOption[] => {
  const prefixes = regions
    .filter((region) => selectedRegionIds.includes(region.id))
    .map((region) => `${region.path}/`);
  const inRegions = (area: PathArea) =>
    prefixes.some((prefix) => area.path.startsWith(prefix));
  const listedIds = new Set(areas.map((area) => area.id));
  const inScope = [
    ...areas.filter(inRegions),
    ...alreadySelected.filter(
      (area) => !listedIds.has(area.id) && inRegions(area),
    ),
  ];
  const nameCounts = new Map<string, number>();
  for (const area of inScope) {
    nameCounts.set(area.name, (nameCounts.get(area.name) ?? 0) + 1);
  }
  return inScope
    .map((area) => ({
      label:
        (nameCounts.get(area.name) ?? 0) > 1 && area.parent
          ? `${area.name} (${area.parent.name})`
          : area.name,
      value: area.id,
    }))
    .sort(byLabel);
};

/**
 * Splits targeted areas into regions (depth 1) and the deeper areas below
 * them (communes, municipalities...), as ids.
 */
export const splitAreaIdsByDepth = (
  areas: ReadonlyArray<DepthArea>,
): { regionIds: string[]; cityIds: string[] } => ({
  regionIds: areas.filter((a) => a.level.depth === 1).map((a) => a.id),
  cityIds: areas.filter((a) => a.level.depth > 1).map((a) => a.id),
});

/**
 * Region and city ids combined into the single list a campaign targets or a
 * location filter sends. The backend keeps the most specific area of each
 * branch: a region picked with a commune inside it means that commune.
 */
export const mergeAreaIds = (
  regionIds: ReadonlyArray<string>,
  cityIds: ReadonlyArray<string>,
): string[] => [...new Set([...regionIds, ...cityIds])];

/**
 * The targeted areas that actually apply: "most specific wins", so an area
 * with another targeted area below it (a region with a commune picked inside
 * it) is left out, and a region without any is wholly open.
 */
export const effectiveAreas = <T extends { id: string; path: string }>(
  areas: ReadonlyArray<T>,
): T[] =>
  areas.filter(
    (area) =>
      !areas.some(
        (other) =>
          other.id !== area.id && other.path.startsWith(`${area.path}/`),
      ),
  );
